import { CategoryCSubmission, QuantitativeSection } from '../../models/category-c-submission';

const EMPTY_12 = (): (number | null)[] => [null, null, null, null, null, null, null, null, null, null, null, null];

const quantitativeTemplate: QuantitativeSection[] = [
  {
    title: '1. Collision/Near Hit',
    editable: true,
    items: [
      { label: 'Collision with an animal resulting in significant damage.', monthlyValues: EMPTY_12() },
      { label: 'Collision with vehicle not at a level crossing.', monthlyValues: EMPTY_12() },
      { label: 'Near hit with a person not at a level crossing.', monthlyValues: EMPTY_12() },
      { label: 'Near hit with a vehicle not at a level crossing.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '2. Proceed Authority Exceeded',
    editable: true,
    items: [
      { label: 'Light rail proceed authority exceedance.', monthlyValues: EMPTY_12() },
      { label: 'Proceed authority within a yard and the train has remained within the yard.', monthlyValues: EMPTY_12() },
      { label: 'Where the rear portion of an otherwise stationary train (excluding a light rail vehicle) rolls back.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '3. Runaway',
    editable: true,
    items: [
      { label: 'Runaway within a yard less than 10m.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '4. Network Rule or Procedure Breach',
    editable: true,
    items: [
      { label: 'Breaches of work scheduling practices and procedures.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '5. Load Irregularity',
    editable: true,
    items: [
      { label: 'Empty wagon in consist.', monthlyValues: EMPTY_12() },
      { label: 'Ineffective/loose load restraints.', monthlyValues: EMPTY_12() },
      { label: 'Load shift.', monthlyValues: EMPTY_12() },
      { label: 'Open doors, hatches, gate and curtains.', monthlyValues: EMPTY_12() },
      { label: 'Underloading or overloading beyond safety tolerances.', monthlyValues: EMPTY_12() },
      { label: 'Uneven distribution of load.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '6. Rolling Stock Irregularity',
    editable: true,
    items: [
      { label: 'Administrative failures that allowed defective rolling stock to be in service.', monthlyValues: EMPTY_12() },
      { label: 'Onboard monitoring equipment failures.', monthlyValues: EMPTY_12() },
      { label: 'Wayside monitoring equipment failures.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '7. Level Crossing Irregularity',
    editable: true,
    items: [
      { label: 'Locking on pedestrian gates failing to fully engage when closed.', monthlyValues: EMPTY_12() },
      { label: 'Missing/damaged control signs and devices at a passive level crossing.', monthlyValues: EMPTY_12() },
      { label: 'Partial operation of boom barriers.', monthlyValues: EMPTY_12() },
      { label: 'Partial operation of visual/audible warning devices.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '8. Track Irregularity',
    editable: true,
    items: [
      { label: 'Broken rail (including fish plates) detected during maintenance/inspection.', monthlyValues: EMPTY_12() },
      { label: 'Horizontal/vertical misalignment detected during maintenance/inspection.', monthlyValues: EMPTY_12() },
      { label: 'Points irregularity detected during maintenance/inspection.', monthlyValues: EMPTY_12() },
      { label: 'Rail defects detected during maintenance/inspection.', monthlyValues: EMPTY_12() },
      { label: 'Track gauge (spread/tight track) detected during maintenance/inspection.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '9. Civil Infrastructure Irregularity',
    editable: true,
    items: [
      { label: 'Bridge strike by road vehicle.', monthlyValues: EMPTY_12() },
      { label: 'Embankment failures and significant track obstruction.', monthlyValues: EMPTY_12() },
      { label: 'Scaffolding collapses.', monthlyValues: EMPTY_12() },
      { label: 'Structural defects of viaducts, bridges, tunnels and station infrastructure.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '10. Electrical Traction Irregularity',
    editable: true,
    items: [
      { label: 'De-wirement or entanglement of overhead wiring.', monthlyValues: EMPTY_12() },
      { label: 'Failure of the overhead wiring.', monthlyValues: EMPTY_12() },
      { label: 'Traction equipment faults.', monthlyValues: EMPTY_12() },
    ],
  },
  {
    title: '11. Person/Train Interface',
    editable: true,
    items: [
      { label: 'Defective rolling stock passenger doors and inter-carriage doors.', monthlyValues: EMPTY_12() },
      { label: 'Door incorrectly opened on the wrong side of the train.', monthlyValues: EMPTY_12() },
      { label: 'Door incorrectly opened when the train is not at or not completely at the platform.', monthlyValues: EMPTY_12() },
      { label: 'Emergency door release not functioning.', monthlyValues: EMPTY_12() },
      { label: 'Persons falling between rolling stock and the platform resulting in minor or no injury.', monthlyValues: EMPTY_12() },
      { label: 'Persons falling from the platform onto the track resulting in minor or no injury.', monthlyValues: EMPTY_12() },
      { label: 'Persons falling whilst boarding and alighting from trains resulting in minor or no injury.', monthlyValues: EMPTY_12() },
      { label: 'Platform screen doors opened without the presence of a train.', monthlyValues: EMPTY_12() },
    ],
  },
];

function cloneTemplate(): QuantitativeSection[] {
  return quantitativeTemplate.map(s => ({
    ...s,
    items: s.items.map(i => ({ ...i, monthlyValues: [...i.monthlyValues] })),
  }));
}

function withData(overrides: Record<string, (number | null)[]>): QuantitativeSection[] {
  const sections = cloneTemplate();
  for (const section of sections) {
    for (const item of section.items) {
      if (overrides[item.label]) {
        item.monthlyValues = overrides[item.label];
      }
    }
  }
  return sections;
}

export class GetCategoryCSubmissions {
  static categoryCSubmissions: CategoryCSubmission[] = [
    {
      id: 1,
      organisationName: 'Aurizon Bulk Central Pty Ltd',
      reportingYear: 2026,
      reportingPeriodStart: '01/07/2025',
      reportingPeriodEnd: '30/06/2026',
      dueDate: '31/12/2026',
      submittedDate: null,
      status: 'Not Started',
      quantitativeReturns: cloneTemplate(),
      history: [],
    },
    {
      id: 2,
      organisationName: 'Aurizon Bulk Central Pty Ltd',
      reportingYear: 2025,
      reportingPeriodStart: '01/07/2024',
      reportingPeriodEnd: '30/06/2025',
      dueDate: '31/12/2025',
      submittedDate: null,
      status: 'In Progress',
      quantitativeReturns: withData({
        'Collision with an animal resulting in significant damage.': [2, 1, 0, 3, 1, 0, 0, 1, 2, 0, 1, 0],
        'Near hit with a person not at a level crossing.': [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        'Load shift.': [0, 1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
        'Ineffective/loose load restraints.': [1, 0, 0, 1, 0, 1, 0, 0, 0, 1, 0, 0],
        'Broken rail (including fish plates) detected during maintenance/inspection.': [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        'Track gauge (spread/tight track) detected during maintenance/inspection.': [0, 1, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0],
      }),
      history: [
        { date: '15/03/2025', user: 'Jason Yuan', action: 'Edited', comment: 'Updated collision and load data' },
        { date: '10/02/2025', user: 'Jason Yuan', action: 'Created', comment: 'Submission created' },
      ],
    },
    {
      id: 3,
      organisationName: 'Aurizon Bulk Central Pty Ltd',
      reportingYear: 2024,
      reportingPeriodStart: '01/07/2023',
      reportingPeriodEnd: '30/06/2024',
      dueDate: '31/12/2024',
      submittedDate: '15/11/2024',
      status: 'Submitted',
      quantitativeReturns: withData({
        'Collision with an animal resulting in significant damage.': [1, 0, 2, 1, 0, 1, 0, 0, 1, 0, 2, 0],
        'Collision with vehicle not at a level crossing.': [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
        'Near hit with a person not at a level crossing.': [0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
        'Proceed authority within a yard and the train has remained within the yard.': [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
        'Breaches of work scheduling practices and procedures.': [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
        'Load shift.': [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        'Empty wagon in consist.': [0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0],
        'Onboard monitoring equipment failures.': [0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
        'Partial operation of boom barriers.': [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
        'Broken rail (including fish plates) detected during maintenance/inspection.': [0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        'Points irregularity detected during maintenance/inspection.': [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        'Rail defects detected during maintenance/inspection.': [1, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0],
        'Bridge strike by road vehicle.': [0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        'Persons falling whilst boarding and alighting from trains resulting in minor or no injury.': [0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0],
      }),
      history: [
        { date: '15/11/2024', user: 'Jason Yuan', action: 'Submitted', comment: 'Annual submission completed' },
        { date: '10/11/2024', user: 'Jason Yuan', action: 'Edited', comment: 'Final review — all categories' },
        { date: '01/10/2024', user: 'Jason Yuan', action: 'Edited', comment: 'Updated track and level crossing data' },
        { date: '15/09/2024', user: 'Jason Yuan', action: 'Created', comment: 'Submission created' },
      ],
    },
    {
      id: 4,
      organisationName: 'Aurizon Bulk Central Pty Ltd',
      reportingYear: 2023,
      reportingPeriodStart: '01/07/2022',
      reportingPeriodEnd: '30/06/2023',
      dueDate: '31/12/2023',
      submittedDate: '20/10/2023',
      status: 'Submitted',
      quantitativeReturns: withData({
        'Collision with an animal resulting in significant damage.': [0, 1, 1, 0, 2, 0, 1, 0, 0, 1, 0, 1],
        'Near hit with a person not at a level crossing.': [0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0],
        'Near hit with a vehicle not at a level crossing.': [0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0],
        'Breaches of work scheduling practices and procedures.': [0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        'Load shift.': [1, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0],
        'Ineffective/loose load restraints.': [0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0],
        'Administrative failures that allowed defective rolling stock to be in service.': [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
        'Missing/damaged control signs and devices at a passive level crossing.': [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
        'Broken rail (including fish plates) detected during maintenance/inspection.': [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        'Track gauge (spread/tight track) detected during maintenance/inspection.': [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0],
        'Structural defects of viaducts, bridges, tunnels and station infrastructure.': [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        'Traction equipment faults.': [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
        'Persons falling between rolling stock and the platform resulting in minor or no injury.': [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
      }),
      history: [
        { date: '20/10/2023', user: 'Jason Yuan', action: 'Submitted', comment: 'Annual submission completed' },
        { date: '05/10/2023', user: 'Jason Yuan', action: 'Edited', comment: 'Updated all categories' },
        { date: '01/08/2023', user: 'Jason Yuan', action: 'Created', comment: 'Submission created' },
      ],
    },
  ];
}
